import { useChat } from '@ai-sdk/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
    AGENT_MODES,
    AGENT_WRITE_DECISION_KEY,
    type AgentMode,
    type AgentStageFileResponse,
    agentToolRequiresConfirmation,
    type AgentWriteDecision,
    DEFAULT_AGENT_MODE,
    PageContext,
} from '@luminovo/api-contracts';
import { getToken } from '@luminovo/auth';
import {
    CircularProgress,
    colorSystem,
    Message,
    PrimaryButton,
    PrimaryIconButton,
    SecondaryButton,
    StatusIcon,
    TertiaryIconButton,
    Text,
    Tooltip,
} from '@luminovo/design-system';
import { resolveBase } from '@luminovo/http-client';
import { Box, InputBase, keyframes } from '@mui/material';
import {
    getToolName,
    isReasoningUIPart,
    isTextUIPart,
    isToolUIPart,
    lastAssistantMessageIsCompleteWithToolCalls,
    type UIMessage,
} from 'ai';
import {
    CircleXIcon,
    CopyIcon,
    FileTextIcon,
    LoaderCircleIcon,
    PaperclipIcon,
    PencilIcon,
    SendIcon,
    SparklesIcon,
    SquareIcon,
    TrashIcon,
    XIcon,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { useCurrentUserDetailsContext } from '@/shared/data/contexts/CurrentUserDetailsContext';
import { useTenantDetailsContext } from '@/shared/data/contexts/TenantDetailsContext';
import { MarkdownRenderer } from '@/shared/ui/MarkdownRenderer';

import { makeStorageKeys } from './agentChatStorage';
import { createAgentChatTransport } from './agentChatTransport';
import { usePageContext } from './usePageContext';

// ============================================================================
// Animations
// ============================================================================

const pulse = keyframes`
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
`;

// ============================================================================
// Local persistence
// ============================================================================

// Cap what we persist — matches the server's message-window cap
// (AGENT_MAX_MODEL_MESSAGES = 60 in gateway/utils/agentMessageWindow.ts), so restored
// history never exceeds what the server would keep on the next send. The QuotaExceededError
// path in saveStoredMessages is the safety net if this ever proves too generous.
const MAX_STORED_MESSAGES = 60;

function loadStoredMessages(key: string): UIMessage[] {
    try {
        const stored = localStorage.getItem(key);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? (parsed as UIMessage[]) : [];
    } catch {
        return [];
    }
}

function saveStoredMessages(key: string, messages: UIMessage[]): void {
    try {
        const toSave = messages.slice(-MAX_STORED_MESSAGES);
        localStorage.setItem(key, JSON.stringify(toSave));
    } catch {
        // Ignore quota / serialization errors; persistence is best-effort.
    }
}

function loadStoredString(key: string): string {
    try {
        return localStorage.getItem(key) ?? '';
    } catch {
        return '';
    }
}

function saveStoredString(key: string, value: string): void {
    try {
        if (value) {
            localStorage.setItem(key, value);
        } else {
            localStorage.removeItem(key);
        }
    } catch {
        // Ignore.
    }
}

function loadStoredMode(key: string): AgentMode {
    const stored = loadStoredString(key);
    return (AGENT_MODES as readonly string[]).includes(stored) ? (stored as AgentMode) : DEFAULT_AGENT_MODE;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Friendly, user-facing noun for a page-context entity type — RfQ surfaces as
 * "project" to match the product copy. Returns undefined for types we have no
 * clean word for, so callers fall back to "this page".
 */
function friendlyEntityNoun(type: string | undefined): string | undefined {
    switch (type) {
        case 'driver':
            return 'driver';
        case 'activity':
            return 'activity';
        case 'expense':
            return 'expense';
        case 'resource':
            return 'resource';
        case 'manufacturingAssemblyDetails':
            return 'assembly';
        case 'manufacturingScenario':
            return 'manufacturing scenario';
        case 'rfq':
        case 'project':
            return 'project';
        case 'part':
            return 'part';
        case 'supplier':
            return 'supplier';
        default:
            return undefined;
    }
}

/**
 * Short label of what the agent currently sees, for the context chip and copy.
 * Uses the page's SECTION title (Manufacturing, BOM, Sourcing, …) from
 * usePageContext — the wording users recognize, not a route artifact.
 */
function pageContextLabel(pageContext: PageContext | undefined): string | undefined {
    return pageContext?.pageTitle;
}

/**
 * Context-aware starter prompts: curated for the highest-value page types,
 * then a generic-but-context-aware fallback for any other entity, then
 * product-level prompts when there's no page context at all.
 */
function getSuggestions(pageContext: PageContext | undefined): string[] {
    const type = pageContext?.entity?.type;

    switch (type) {
        case 'driver':
            return [
                'Which parts is this driver counting, and is that what you expect?',
                'Explain this driver’s logic in plain language.',
                'Is anything in this driver’s setup likely wrong?',
            ];
        case 'activity':
            return [
                'Explain what this activity costs and which drivers it uses.',
                'Does this activity have everything it needs to calculate correctly?',
            ];
        case 'manufacturingAssemblyDetails':
            return [
                'What’s driving the manufacturing cost of this assembly?',
                'Which drivers on this assembly have data-quality problems?',
            ];
        case 'rfq':
        case 'project':
            return [
                'Summarize this project: assemblies, BOM status, and where it stands.',
                'What’s blocking this project from being quoted?',
            ];
    }

    const noun = friendlyEntityNoun(type);
    if (noun) {
        return [
            `Tell me about this ${noun} and flag anything that needs attention.`,
            `What can you help me do with this ${noun}?`,
        ];
    }

    if (pageContext?.pageTitle) {
        return [
            'Summarize what I’m looking at and flag anything that needs attention.',
            'What can you help me with on this page?',
        ];
    }

    return ['What can you help me with across this project?', 'Find a project, BOM, part, or supplier for me.'];
}

const TOOL_STATE: Record<string, { label: string; done: boolean }> = {
    'input-streaming': { label: 'preparing', done: false },
    'input-available': { label: 'running', done: false },
    'output-available': { label: 'done', done: true },
    'output-error': { label: 'failed', done: true },
};

function prettifyToolName(name: string): string {
    return name.replace(/_/g, ' ');
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Displays the keyboard shortcut for opening/closing Lumi.
 * Shows ⌘/Ctrl + I to cover both Mac and Windows/Linux.
 */
const KeyboardShortcut: React.FC = () => {
    const kbdStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '22px',
        height: '20px',
        padding: '0 6px',
        fontSize: '13px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontWeight: 500,
        color: colorSystem.neutral[7],
        backgroundColor: colorSystem.neutral[1],
        border: `1px solid ${colorSystem.neutral[3]}`,
        borderRadius: '4px',
        boxShadow: `0 1px 0 ${colorSystem.neutral[3]}`,
    };

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
            <kbd style={kbdStyle}>⌘/Ctrl</kbd>
            <kbd style={kbdStyle}>i</kbd>
        </span>
    );
};

const LumiLogo: React.FC = () => (
    <Box
        sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${colorSystem.primary[5]} 0%, ${colorSystem.primary[7]} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        }}
    >
        <SparklesIcon size={16} color="white" />
    </Box>
);

const ThinkingDots: React.FC = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', p: '12px 24px' }}>
        {[0, 1, 2].map((i) => (
            <Box
                key={i}
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: colorSystem.primary[5],
                    animation: `${pulse} 1.4s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                }}
            />
        ))}
    </Box>
);

/** A live "the agent is using a tool" chip — the heart of the watch-it-work UX. */
const ToolCallChip: React.FC<{ name: string; state: string; output?: unknown }> = ({ name, state, output }) => {
    // A confirmation-gated tool the user already approved/declined: reflect that
    // instead of the raw stream state.
    const decision = readWriteDecision(output);
    const declined = decision === 'rejected';
    const done = decision !== undefined || (TOOL_STATE[state]?.done ?? false);
    const label = decision === 'approved' ? t`approved` : declined ? t`declined` : (TOOL_STATE[state]?.label ?? state);

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '8px',
                backgroundColor: colorSystem.neutral[1],
                border: `1px solid ${colorSystem.neutral[2]}`,
                margin: '4px 0',
            }}
        >
            {declined ? (
                <CircleXIcon size={14} color={colorSystem.neutral[5]} />
            ) : done ? (
                <StatusIcon status="success" size={14} />
            ) : (
                <LoaderCircleIcon size={14} color={colorSystem.primary[6]} />
            )}
            <Text variant="caption" style={{ color: colorSystem.neutral[7], fontFamily: 'monospace' }}>
                {prettifyToolName(name)}
            </Text>
            <Text variant="caption" style={{ color: colorSystem.neutral[5] }}>
                {label}
            </Text>
        </Box>
    );
};

/** Reads the user's write decision marker off a tool part's output, if present. */
function readWriteDecision(output: unknown): AgentWriteDecision | undefined {
    if (output && typeof output === 'object' && AGENT_WRITE_DECISION_KEY in output) {
        const value = (output as Record<string, unknown>)[AGENT_WRITE_DECISION_KEY];
        if (value === 'approved' || value === 'rejected') {
            return value;
        }
    }
    return undefined;
}

/**
 * The write-confirmation card: shown when the agent wants to run a write
 * operation and is paused awaiting the user's approval. Shows a short, plain
 * description of the change (no JSON / URLs) — the specifics are in the agent's
 * message just above it, which the prompt requires it to state before writing.
 */
const WriteConfirmCard: React.FC<{
    name: string;
    input: unknown;
    onDecision: (decision: AgentWriteDecision) => void;
}> = ({ name, input, onDecision }) => {
    const description = describeWriteAction(name, input);
    return (
        <Box
            sx={{
                margin: '8px 0',
                padding: '12px 14px',
                borderRadius: '12px',
                border: `1px solid ${colorSystem.yellow[4]}`,
                backgroundColor: colorSystem.yellow[1],
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <PencilIcon size={18} color={colorSystem.yellow[7]} />
                <Text variant="body-small-semibold" style={{ color: colorSystem.neutral[8] }}>
                    <Trans>Approve this change?</Trans>
                </Text>
            </Box>
            <Text
                variant="body-small"
                style={{ color: colorSystem.neutral[7], display: 'block', margin: '2px 0 10px' }}
            >
                {description}
            </Text>
            <Box sx={{ display: 'flex', gap: '8px' }}>
                <PrimaryButton size="small" onClick={() => onDecision('approved')}>
                    <Trans>Approve</Trans>
                </PrimaryButton>
                <SecondaryButton size="small" onClick={() => onDecision('rejected')}>
                    <Trans context="Lumi edit approval">Decline</Trans>
                </SecondaryButton>
            </Box>
        </Box>
    );
};

const WRITE_VERBS: Record<string, string> = {
    POST: 'Create',
    PUT: 'Update',
    PATCH: 'Update',
    DELETE: 'Delete',
};

/**
 * A short, plain-English description of a pending write for the confirmation
 * card — never raw JSON, URLs, or endpoint paths. For the generic
 * call_write_operation we turn the operation (e.g. "PATCH /user-drivers/:id")
 * into "Update a user driver"; for named write tools we prettify the tool name.
 * Falls back to a neutral phrase when we can't tell.
 */
function describeWriteAction(name: string, input: unknown): string {
    if (name === 'call_write_operation') {
        const operation =
            input && typeof input === 'object' && typeof (input as { operation?: unknown }).operation === 'string'
                ? (input as { operation: string }).operation
                : '';
        const match = operation.match(/^(POST|PUT|PATCH|DELETE)\s+\/([^/?:]+)/i);
        if (match) {
            const verb = WRITE_VERBS[match[1].toUpperCase()] ?? 'Change';
            const resource = match[2].replace(/-/g, ' ').replace(/s$/, '');
            return `${verb} a ${resource}`;
        }
        return 'Make a change to your data';
    }
    // Named write tools, e.g. "create_report" → "Create report".
    const pretty = prettifyToolName(name);
    return pretty.charAt(0).toUpperCase() + pretty.slice(1);
}

interface WriteDecisionArgs {
    tool: string;
    toolCallId: string;
    decision: AgentWriteDecision;
}

/**
 * Permission-mode selector. read_only (default) confirms writes + code-mode;
 * writes_allowed runs everything without prompting (highlighted in a warning tint
 * so the elevated mode is obvious).
 */
const ModeToggle: React.FC<{ mode: AgentMode; onChange: (mode: AgentMode) => void }> = ({ mode, onChange }) => {
    // Display labels describe the behavior; the internal values stay read_only /
    // writes_allowed (used by the request schema + telemetry).
    const options: Array<{ value: AgentMode; label: React.ReactNode }> = [
        { value: 'read_only', label: <Trans>Ask before edits</Trans> },
        { value: 'writes_allowed', label: <Trans>Edit automatically</Trans> },
    ];
    return (
        <Box
            sx={{
                display: 'inline-flex',
                borderRadius: '8px',
                border: `1px solid ${colorSystem.neutral[2]}`,
                overflow: 'hidden',
                flexShrink: 0,
            }}
        >
            {options.map((option) => {
                const active = mode === option.value;
                const elevated = option.value === 'writes_allowed';
                const bg = active ? (elevated ? colorSystem.yellow[2] : colorSystem.primary[1]) : 'transparent';
                const fg = active
                    ? elevated
                        ? colorSystem.yellow[8]
                        : colorSystem.primary[7]
                    : colorSystem.neutral[5];
                return (
                    <Box
                        key={option.value}
                        onClick={() => onChange(option.value)}
                        sx={{ cursor: 'pointer', padding: '3px 10px', backgroundColor: bg }}
                    >
                        <Text variant="caption" style={{ color: fg, fontWeight: active ? 600 : 400 }}>
                            {option.label}
                        </Text>
                    </Box>
                );
            })}
        </Box>
    );
};

/** Renders one message's ordered parts: text (markdown) + live tool-call chips. */
const MessageView: React.FC<{
    message: UIMessage;
    mode: AgentMode;
    onWriteDecision: (args: WriteDecisionArgs) => void;
    onCopy: (message: UIMessage) => void;
}> = ({ message, mode, onWriteDecision, onCopy }) => {
    const isUser = message.role === 'user';
    // Web-search citations arrive as `source-url` parts; group them into a footer
    // (they're skipped inline by the part map below) so web-sourced answers are
    // attributable.
    const sources = collectSources(message.parts);
    // The copy button hangs off the message body for assistant turns that actually
    // rendered some text — no point offering to copy an empty message.
    const canCopy = !isUser && message.parts.some((p) => isTextUIPart(p) && p.text.length > 0);

    return (
        <Box sx={{ p: '8px 24px' }}>
            {message.parts.map((part, index) => {
                if (isTextUIPart(part)) {
                    if (!part.text) {
                        return null;
                    }
                    return isUser ? (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Box
                                sx={{
                                    maxWidth: '85%',
                                    backgroundColor: colorSystem.primary[6],
                                    borderRadius: '16px',
                                    padding: '8px 12px',
                                }}
                            >
                                <Text variant="body-small" style={{ color: 'white', lineHeight: 1.5 }}>
                                    {part.text}
                                </Text>
                            </Box>
                        </Box>
                    ) : (
                        <MarkdownRenderer key={index} markdown={part.text} />
                    );
                }

                if (isToolUIPart(part)) {
                    const name = getToolName(part);
                    const toolPart = part as unknown as {
                        state: string;
                        toolCallId: string;
                        input?: unknown;
                        output?: unknown;
                    };
                    // A confirmation-gated tool paused awaiting the user's decision.
                    if (agentToolRequiresConfirmation(name, mode) && toolPart.state === 'input-available') {
                        return (
                            <WriteConfirmCard
                                key={index}
                                name={name}
                                input={toolPart.input}
                                onDecision={(decision) =>
                                    onWriteDecision({ tool: name, toolCallId: toolPart.toolCallId, decision })
                                }
                            />
                        );
                    }
                    return <ToolCallChip key={index} name={name} state={toolPart.state} output={toolPart.output} />;
                }

                if (isReasoningUIPart(part) && part.text) {
                    return (
                        <Text
                            key={index}
                            variant="caption"
                            style={{ color: colorSystem.neutral[5], fontStyle: 'italic', display: 'block' }}
                        >
                            {part.text}
                        </Text>
                    );
                }

                return null;
            })}
            {sources.length > 0 && <SourcesFooter sources={sources} />}
            {canCopy && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginTop: '2px' }}>
                    <Tooltip title={t`Copy`} arrow placement="top">
                        <TertiaryIconButton aria-label={t`Copy`} size="small" onClick={() => onCopy(message)}>
                            <CopyIcon />
                        </TertiaryIconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

/** Human-readable byte count for the attachment chip — "342 KB", "12 MB", etc. */
function formatBytes(bytes: number | undefined): string {
    if (bytes === undefined || bytes < 1024) {
        return `${bytes ?? 0} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Chip strip above the input for pending + successfully staged attachments. */
const AttachmentStrip: React.FC<{
    attachments: AgentStageFileResponse[];
    uploading: readonly string[];
    onRemove: (handle: string) => void;
}> = ({ attachments, uploading, onRemove }) => {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
            {attachments.map((att) => (
                <Box
                    key={att.handle}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 4px 4px 10px',
                        borderRadius: '8px',
                        border: `1px solid ${colorSystem.neutral[3]}`,
                        backgroundColor: colorSystem.neutral[1],
                        maxWidth: '220px',
                    }}
                >
                    <FileTextIcon size={14} color={colorSystem.primary[6]} style={{ flexShrink: 0 }} />
                    <Text
                        variant="caption"
                        style={{
                            color: colorSystem.neutral[8],
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minWidth: 0,
                        }}
                    >
                        {att.filename}
                    </Text>
                    <Text variant="caption" style={{ color: colorSystem.neutral[5], flexShrink: 0 }}>
                        {formatBytes(att.byteCount)}
                    </Text>
                    <TertiaryIconButton
                        aria-label={t`Remove attachment`}
                        size="small"
                        onClick={() => onRemove(att.handle)}
                    >
                        <XIcon />
                    </TertiaryIconButton>
                </Box>
            ))}
            {uploading.map((name) => (
                <Box
                    key={`uploading:${name}`}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        borderRadius: '8px',
                        border: `1px dashed ${colorSystem.neutral[3]}`,
                        backgroundColor: colorSystem.neutral[0],
                        maxWidth: '220px',
                    }}
                >
                    <CircularProgress size="small" sx={{ color: colorSystem.primary[6], flexShrink: 0 }} />
                    <Text
                        variant="caption"
                        style={{
                            color: colorSystem.neutral[6],
                            fontStyle: 'italic',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minWidth: 0,
                        }}
                    >
                        {name}
                    </Text>
                    <Text variant="caption" style={{ color: colorSystem.neutral[5], flexShrink: 0 }}>
                        <Trans>Uploading…</Trans>
                    </Text>
                </Box>
            ))}
        </Box>
    );
};

/** Collects unique web-search source citations (source-url parts) from a message. */
function collectSources(parts: UIMessage['parts']): Array<{ url: string; title?: string }> {
    const seen = new Set<string>();
    const sources: Array<{ url: string; title?: string }> = [];
    for (const part of parts) {
        if (part.type === 'source-url') {
            const { url, title } = part as { url: string; title?: string };
            if (url && !seen.has(url)) {
                seen.add(url);
                sources.push({ url, title });
            }
        }
    }
    return sources;
}

function hostnameOf(url: string): string {
    try {
        return new URL(url).hostname;
    } catch {
        return url;
    }
}

/** Renders web-search citations as a compact "Sources" footer with links. */
const SourcesFooter: React.FC<{ sources: Array<{ url: string; title?: string }> }> = ({ sources }) => (
    <Box sx={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${colorSystem.neutral[2]}` }}>
        <Text
            variant="caption"
            style={{
                color: colorSystem.neutral[5],
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'block',
                marginBottom: '4px',
            }}
        >
            <Trans>Sources</Trans>
        </Text>
        {sources.map((source, index) => (
            <a
                key={index}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'block', color: colorSystem.primary[6], fontSize: '12px', lineHeight: 1.6 }}
            >
                {source.title || hostnameOf(source.url)}
            </a>
        ))}
    </Box>
);

// ============================================================================
// Main component
// ============================================================================

interface LumiAgentPanelProps {
    onClose: () => void;
    isWebSearchEnabled: boolean;
}

export function LumiAgentPanel({ onClose, isWebSearchEnabled }: LumiAgentPanelProps): React.ReactElement {
    const pageContext = usePageContext();
    const contextLabel = pageContextLabel(pageContext);
    const { tenantDetails } = useTenantDetailsContext();
    const { user } = useCurrentUserDetailsContext();
    const { enqueueSnackbar } = useSnackbar();

    // Scope persistence per tenant + user so switching accounts doesn't leak chat state.
    // Foreign-scope cleanup lives in `useLumi` — it runs whenever the global command
    // palette is mounted (i.e. whenever the user is authenticated), so it fires on
    // every session boot and identity change regardless of whether the panel is open.
    const storageKeys = React.useMemo(
        () => makeStorageKeys(tenantDetails.tenant, user.id),
        [tenantDetails.tenant, user.id],
    );

    const [input, setInput] = React.useState<string>(() => loadStoredString(storageKeys.draft));
    // Permission mode: read_only (default) confirms writes + code-mode; writes_allowed
    // runs everything without prompting. Sent with each message; enforced server-side.
    const [mode, setMode] = React.useState<AgentMode>(() => loadStoredMode(storageKeys.mode));
    const initialMessages = React.useMemo(() => loadStoredMessages(storageKeys.messages), [storageKeys.messages]);
    const messagesEndRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);

    // Files the user has staged for the next send. Not persisted — the staging
    // handles expire quickly, so re-attaching after a reload is safer than
    // restoring a stale reference. Cleared on send and on reset.
    const [attachments, setAttachments] = React.useState<AgentStageFileResponse[]>([]);
    // Names of files currently being uploaded, shown as a pending chip until the
    // server returns their handle. A single Set covers multi-file selection.
    const [uploadingFiles, setUploadingFiles] = React.useState<readonly string[]>([]);

    // Stable transport: `useChat` keeps the one it was first given.
    const transport = React.useMemo(() => createAgentChatTransport(tenantDetails.tenant), [tenantDetails.tenant]);

    const { messages, sendMessage, status, stop, error, setMessages, addToolOutput } = useChat({
        transport,
        // Hydrate from localStorage so closing/reopening the panel doesn't lose the chat.
        messages: initialMessages,
        // Once the user has decided every pending write confirmation, auto-resubmit
        // so the gateway runs the approved writes and the model continues.
        sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    });

    // Record the user's approve/reject decision on a paused write tool call. The
    // gateway reads this marker and executes (or declines) the write.
    const onWriteDecision = React.useCallback(
        ({ tool, toolCallId, decision }: { tool: string; toolCallId: string; decision: AgentWriteDecision }) => {
            void (addToolOutput as (args: { tool: string; toolCallId: string; output: unknown }) => Promise<void>)({
                tool,
                toolCallId,
                output: { [AGENT_WRITE_DECISION_KEY]: decision },
            });
        },
        [addToolOutput],
    );

    // Auto-approve pending confirmation cards when the user switches to
    // writes_allowed. Without this, MessageView's card-render condition depends
    // on the CURRENT mode, so flipping the toggle while a code_mode_exec (or
    // any write) is paused at `input-available` hides the card, leaving the SDK
    // stuck waiting for a decision the UI can no longer provide.
    //
    // "Stop asking me" is the natural read of the toggle, so approving the
    // pending write matches user intent. Any subsequent write in the same
    // stream lands here again (mode is still writes_allowed) and auto-approves,
    // so the whole run advances without further prompting.
    React.useEffect(() => {
        if (mode !== 'writes_allowed') return;
        for (const message of messages) {
            if (message.role !== 'assistant') continue;
            for (const part of message.parts) {
                if (!isToolUIPart(part)) continue;
                const toolPart = part as unknown as { state: string; toolCallId: string; output?: unknown };
                if (toolPart.state !== 'input-available') continue;
                // Skip if a decision is already recorded (idempotency across re-renders).
                const output = toolPart.output;
                if (output && typeof output === 'object' && AGENT_WRITE_DECISION_KEY in output) continue;
                const name = getToolName(part);
                // 'read_only' is the predicate for "is this a confirmation-gated tool" —
                // it only returns true for writes / code_mode_exec, which is what we
                // want to auto-approve. Read-only tools never reach this branch.
                if (!agentToolRequiresConfirmation(name, 'read_only')) continue;
                void (addToolOutput as (args: { tool: string; toolCallId: string; output: unknown }) => Promise<void>)({
                    tool: name,
                    toolCallId: toolPart.toolCallId,
                    output: { [AGENT_WRITE_DECISION_KEY]: 'approved' },
                });
            }
        }
    }, [mode, messages, addToolOutput]);

    const isBusy = status === 'submitted' || status === 'streaming';
    const hasMessages = messages.length > 0;

    React.useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, status]);

    // Persist messages when not mid-stream — writing every streamed token would
    // both thrash localStorage and store half-finished tool calls.
    React.useEffect(() => {
        if (isBusy) return;
        saveStoredMessages(storageKeys.messages, messages);
    }, [messages, isBusy, storageKeys.messages]);

    React.useEffect(() => {
        saveStoredString(storageKeys.draft, input);
    }, [input, storageKeys.draft]);

    React.useEffect(() => {
        saveStoredString(storageKeys.mode, mode);
    }, [mode, storageKeys.mode]);

    // Auto-focus the input each time the panel mounts (it's remounted on open).
    React.useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
    }, []);

    const submit = React.useCallback(
        (text: string) => {
            const trimmed = text.trim();
            // Allow empty text if there are attachments — the model can still act on
            // them (e.g. "summarize this file"). Block sends while uploading so a
            // half-staged file doesn't miss the turn.
            if (isBusy || uploadingFiles.length > 0) return;
            if (!trimmed && attachments.length === 0) return;
            setInput('');
            // Snapshot the attachments for this send and clear the input strip —
            // they belong to THIS turn, not the next one.
            const attachmentsForTurn = attachments;
            setAttachments([]);
            // Prepend one "📎 filename" line per attachment to the user's text so the
            // bubble shows what they attached (and localStorage restores it). The
            // server ALSO injects a technical marker with handles/paths further down
            // for the model — this line is just human-facing and cheap redundancy.
            const attachmentLines = attachmentsForTurn.map(({ filename }) => `📎 ${filename}`).join('\n');
            const displayText = [attachmentLines, trimmed].filter(Boolean).join('\n\n');
            // Send the CURRENT page context + permission mode with each message so the
            // agent always knows what the user is looking at and what it may do.
            void sendMessage(
                { text: displayText },
                {
                    body: {
                        pageContext,
                        mode,
                        ...(attachmentsForTurn.length > 0
                            ? {
                                  attachments: attachmentsForTurn.map(({ handle, filename, path, byteCount }) => ({
                                      handle,
                                      filename,
                                      path,
                                      ...(byteCount === undefined ? {} : { byteCount }),
                                  })),
                              }
                            : {}),
                    },
                },
            );
        },
        [isBusy, sendMessage, pageContext, mode, attachments, uploadingFiles.length],
    );

    // Reset both the in-memory chat and its persisted mirror. Mode stays (it's a
    // user preference, not a per-conversation value). Also drops any attachments
    // the user staged but hasn't sent yet — their staging handles expire quickly.
    const handleReset = React.useCallback(() => {
        setMessages([]);
        setInput('');
        setAttachments([]);
        try {
            localStorage.removeItem(storageKeys.messages);
            localStorage.removeItem(storageKeys.draft);
        } catch {
            // Ignore.
        }
    }, [setMessages, storageKeys.messages, storageKeys.draft]);

    // Uploads one file to the /agent/stage-file endpoint; on success adds the
    // resulting handle+metadata to the attachments strip. Fails soft — a single
    // file failing doesn't block the batch or the send.
    const uploadOneFile = React.useCallback(
        async (file: File): Promise<void> => {
            const token = getToken();
            const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
            if (resolveBase('gateway').includes('localhost')) {
                headers['validated-token'] = btoa(JSON.stringify({ tenant: tenantDetails.tenant }));
            }
            const form = new FormData();
            form.append('file', file, file.name);
            const res = await fetch(`${resolveBase('gateway')}/gateway/llm/agent/stage-file`, {
                method: 'POST',
                headers,
                body: form,
            });
            if (!res.ok) {
                const body = await res.text().catch(() => '');
                throw new Error(body || `upload failed (${res.status})`);
            }
            const parsed = (await res.json()) as AgentStageFileResponse;
            setAttachments((prev) => [...prev, parsed]);
        },
        [tenantDetails.tenant],
    );

    const handleFilesSelected = React.useCallback(
        async (files: FileList | null): Promise<void> => {
            if (!files || files.length === 0) return;
            const list = Array.from(files);
            setUploadingFiles((prev) => [...prev, ...list.map((f) => f.name)]);
            for (const file of list) {
                try {
                    await uploadOneFile(file);
                } catch (err) {
                    enqueueSnackbar(t`Failed to attach ${file.name}: ${err instanceof Error ? err.message : ''}`, {
                        variant: 'error',
                    });
                } finally {
                    setUploadingFiles((prev) => prev.filter((n) => n !== file.name));
                }
            }
        },
        [uploadOneFile, enqueueSnackbar],
    );

    const handleRemoveAttachment = React.useCallback((handle: string) => {
        setAttachments((prev) => prev.filter((a) => a.handle !== handle));
    }, []);

    const openFilePicker = React.useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    // Retry the last user turn after a stream failure. Drop everything from the
    // last user message onwards (which includes any partial assistant reply that
    // errored) and resend the same text.
    const handleRetry = React.useCallback(() => {
        if (isBusy) return;
        let lastUserIdx = -1;
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === 'user') {
                lastUserIdx = i;
                break;
            }
        }
        if (lastUserIdx < 0) return;
        const lastText = messages[lastUserIdx].parts
            .filter(isTextUIPart)
            .map((p) => p.text)
            .join('\n');
        if (!lastText) return;
        setMessages(messages.slice(0, lastUserIdx));
        submit(lastText);
    }, [isBusy, messages, setMessages, submit]);

    const onCopyMessage = React.useCallback(
        (message: UIMessage) => {
            const text = message.parts
                .filter(isTextUIPart)
                .map((p) => p.text)
                .join('\n\n');
            if (!text) return;
            navigator.clipboard
                .writeText(text)
                .then(() => enqueueSnackbar(t`Copied to clipboard`, { variant: 'success', autoHideDuration: 2000 }))
                .catch(() => enqueueSnackbar(t`Copy failed`, { variant: 'error' }));
        },
        [enqueueSnackbar],
    );

    const showThinking = status === 'submitted' || (status === 'streaming' && lastMessageHasNoText(messages));

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                backgroundColor: colorSystem.neutral.white,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 16px 16px 20px',
                    borderBottom: `1px solid ${colorSystem.neutral[2]}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <LumiLogo />
                    <Text variant="h4" style={{ color: colorSystem.neutral[8] }}>
                        <Trans>Lumi, your AI assistant</Trans>
                    </Text>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Tooltip title={t`Clear chat`} arrow placement="bottom">
                    <TertiaryIconButton
                        aria-label={t`Clear chat`}
                        size="small"
                        onClick={handleReset}
                        disabled={!hasMessages}
                    >
                        <TrashIcon />
                    </TertiaryIconButton>
                </Tooltip>
                <Tooltip title={t`Close`} arrow placement="bottom">
                    <TertiaryIconButton aria-label={t`Close`} size="small" onClick={onClose}>
                        <XIcon />
                    </TertiaryIconButton>
                </Tooltip>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                {!hasMessages ? (
                    <WelcomeState
                        pageContext={pageContext}
                        isWebSearchEnabled={isWebSearchEnabled}
                        onSuggestionClick={submit}
                    />
                ) : (
                    <Box sx={{ py: 1 }}>
                        {messages.map((message) => (
                            <MessageView
                                key={message.id}
                                message={message}
                                mode={mode}
                                onWriteDecision={onWriteDecision}
                                onCopy={onCopyMessage}
                            />
                        ))}
                        {showThinking && <ThinkingDots />}
                        {error && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    p: '8px 24px',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Text variant="body-small" style={{ color: colorSystem.red[6] }}>
                                    <Trans>Something went wrong.</Trans>
                                </Text>
                                <SecondaryButton size="small" onClick={handleRetry} disabled={isBusy}>
                                    <Trans>Retry</Trans>
                                </SecondaryButton>
                            </Box>
                        )}
                        <div ref={messagesEndRef} />
                    </Box>
                )}
            </Box>

            {/* Input */}
            <Box
                sx={{
                    padding: '16px 20px',
                    borderTop: `1px solid ${colorSystem.neutral[2]}`,
                    backgroundColor: colorSystem.neutral[0],
                    flexShrink: 0,
                }}
            >
                {/* Page-context indicator (left) + permission-mode toggle (right). */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        marginBottom: '8px',
                        minHeight: '24px',
                    }}
                >
                    {contextLabel ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                            <SparklesIcon size={14} color={colorSystem.primary[6]} style={{ flexShrink: 0 }} />
                            <Text variant="caption" style={{ color: colorSystem.neutral[6] }}>
                                <Trans>Viewing: {contextLabel}</Trans>
                            </Text>
                        </Box>
                    ) : (
                        <span />
                    )}
                    <ModeToggle mode={mode} onChange={setMode} />
                </Box>
                {(attachments.length > 0 || uploadingFiles.length > 0) && (
                    <AttachmentStrip
                        attachments={attachments}
                        uploading={uploadingFiles}
                        onRemove={handleRemoveAttachment}
                    />
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    accept=".pdf,.csv,.xls,.xlsx,.txt,.json,.png,.jpg,.jpeg,.webp,application/pdf,text/*,image/*"
                    onChange={(e) => {
                        void handleFilesSelected(e.target.files);
                        // Reset so re-picking the same file fires onChange again.
                        e.target.value = '';
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: colorSystem.neutral.white,
                        borderRadius: '12px',
                        border: `1px solid ${colorSystem.neutral[3]}`,
                        padding: '8px 8px 8px 12px',
                    }}
                >
                    <Tooltip title={t`Attach files`} arrow placement="top">
                        <span>
                            <TertiaryIconButton
                                aria-label={t`Attach files`}
                                size="small"
                                onClick={openFilePicker}
                                disabled={isBusy || uploadingFiles.length > 0}
                            >
                                <PaperclipIcon />
                            </TertiaryIconButton>
                        </span>
                    </Tooltip>
                    <InputBase
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                submit(input);
                            }
                        }}
                        placeholder={t`Ask about this page…`}
                        multiline
                        maxRows={4}
                        inputRef={inputRef}
                        sx={{ flex: 1, fontSize: '14px', lineHeight: 1.5, color: colorSystem.neutral[9] }}
                    />
                    {isBusy ? (
                        <Tooltip title={t`Stop`} arrow placement="top">
                            <PrimaryIconButton
                                aria-label={t`Stop`}
                                size="medium"
                                onClick={() => void stop()}
                                sx={{ borderRadius: '8px' }}
                            >
                                <SquareIcon fill="currentColor" />
                            </PrimaryIconButton>
                        </Tooltip>
                    ) : (
                        <PrimaryIconButton
                            aria-label={t`Send message`}
                            size="medium"
                            onClick={() => submit(input)}
                            disabled={uploadingFiles.length > 0 || (!input.trim() && attachments.length === 0)}
                            sx={{ borderRadius: '8px' }}
                        >
                            <SendIcon />
                        </PrimaryIconButton>
                    )}
                </Box>
                <Text
                    variant="caption"
                    style={{ display: 'block', textAlign: 'center', color: colorSystem.neutral[5], marginTop: '8px' }}
                >
                    <Trans>Lumi is experimental and can take actions on your data.</Trans>
                </Text>
            </Box>
        </Box>
    );
}

function lastMessageHasNoText(messages: UIMessage[]): boolean {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') {
        return true;
    }
    return !last.parts.some((part) => isTextUIPart(part) && part.text.length > 0);
}

const WelcomeState: React.FC<{
    pageContext: PageContext | undefined;
    isWebSearchEnabled: boolean;
    onSuggestionClick: (s: string) => void;
}> = ({ pageContext, isWebSearchEnabled, onSuggestionClick }) => {
    const label = pageContextLabel(pageContext);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', padding: '40px 24px', gap: '24px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        background: `linear-gradient(135deg, ${colorSystem.primary[5]} 0%, ${colorSystem.primary[7]} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '16px',
                    }}
                >
                    <SparklesIcon size={32} color="white" />
                </Box>
                <Text variant="h3" style={{ color: colorSystem.neutral[8] }}>
                    <Trans>Lumi</Trans>
                </Text>
                <Text variant="body-small" style={{ color: colorSystem.neutral[5] }}>
                    {label ? (
                        <Trans>I can see your {label} page — ask about it, or tell me to make a change.</Trans>
                    ) : (
                        <Trans>
                            Your AI assistant for Luminovo — open a project, BOM, or manufacturing page and I can see
                            it.
                        </Trans>
                    )}
                </Text>
            </Box>

            {/* Tips: keyboard shortcut, and a web-search-disabled notice when applicable. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Message
                    variant="blue"
                    attention="high"
                    size="small"
                    message={
                        <Trans>
                            Press <KeyboardShortcut /> to open or close Lumi.
                        </Trans>
                    }
                />

                {!isWebSearchEnabled && (
                    <Message
                        variant="yellow"
                        attention="high"
                        size="small"
                        message={
                            <Trans>
                                Web search is disabled to protect your data. <br />
                                <a href="mailto:support@luminovo.com" style={{ color: colorSystem.yellow[7] }}>
                                    Contact support
                                </a>{' '}
                                to enable it.
                            </Trans>
                        }
                    />
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Text
                    variant="caption"
                    style={{ color: colorSystem.neutral[5], textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                    <Trans>Try asking</Trans>
                </Text>
                {getSuggestions(pageContext).map((suggestion, i) => (
                    <Box
                        key={i}
                        onClick={() => onSuggestionClick(suggestion)}
                        sx={{
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: `1px solid ${colorSystem.neutral[2]}`,
                            cursor: 'pointer',
                            '&:hover': { borderColor: colorSystem.primary[4], backgroundColor: colorSystem.primary[1] },
                        }}
                    >
                        <Text variant="body-small" style={{ color: colorSystem.neutral[7], lineHeight: 1.4 }}>
                            {suggestion}
                        </Text>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
